/**
 * Markdown → HTML, written the way CommonMark's reference renderer writes it (`<hr />`,
 * `<br />`, one block per line), with GitHub's table markup.
 *
 * Every piece of text is escaped. Raw HTML only reaches the output when `allowHtml` is
 * on, and then only through `sanitizeHtml` — the caller's job, see `convertMarkdown`.
 * Link and image URLs must pass `isSafeUrl`: a `javascript:` or `data:` link keeps its
 * text and loses its address.
 */

import {
  type BlockNode,
  parseBlocks,
  type TableAlign,
} from "@/lib/tool-kit/developer/markdown/blocks";
import {
  type InlineNode,
  type InlineOptions,
  parseInlines,
  type ReferenceMap,
} from "@/lib/tool-kit/developer/markdown/inlines";
import { escapeHtml, isSafeUrl } from "@/lib/tool-kit/developer/markdown/text";

export interface MarkdownOptions {
  /** Tables, strikethrough and bare-URL autolinks. */
  gfm: boolean;
  /** Pass raw HTML through (it must then be sanitised). Off escapes it as text. */
  allowHtml: boolean;
}

const RE_WHITESPACE = /\s+/;
const RE_LANGUAGE_PREFIX = /^language-/;
/** Emphasis nested deeper than this is written as plain text rather than recursed into. */
const MAX_INLINE_DEPTH = 100;

/**
 * Output in chunks. `cr` needs the last character written, and reading it back off a
 * growing string makes the engine flatten that string every time — quadratic on a long
 * document — so the last character is tracked instead.
 */
class Writer {
  readonly parts: string[] = [];
  last = "";

  lit(text: string): void {
    if (text.length > 0) {
      this.parts.push(text);
      this.last = text.at(-1) ?? "";
    }
  }

  /** A newline, unless the output already ends with one. */
  cr(): void {
    if (this.parts.length > 0 && this.last !== "\n") {
      this.lit("\n");
    }
  }

  toString(): string {
    return this.parts.join("");
  }
}

interface RenderContext {
  writer: Writer;
  refs: ReferenceMap;
  inline: InlineOptions;
}

/** The text inside a node with the markup dropped — an image's `alt`. Iterative. */
function plainText(node: InlineNode): string {
  let text = "";
  let current: InlineNode | null = node.firstChild;
  while (current !== null) {
    if (
      current.type === "text" ||
      current.type === "code" ||
      current.type === "html"
    ) {
      text += current.literal;
    } else if (current.type === "softbreak" || current.type === "hardbreak") {
      text += "\n";
    }
    if (current.firstChild) {
      current = current.firstChild;
      continue;
    }
    let climb: InlineNode | null = current;
    while (climb !== null && climb !== node && climb.next === null) {
      climb = climb.parent;
    }
    current = climb === null || climb === node ? null : climb.next;
  }
  return text;
}

function renderChildren(node: InlineNode, writer: Writer, depth: number): void {
  for (let child = node.firstChild; child; child = child.next) {
    renderInline(child, writer, depth + 1);
  }
}

function renderLink(node: InlineNode, writer: Writer, depth: number): void {
  if (!isSafeUrl(node.destination)) {
    renderChildren(node, writer, depth);
    return;
  }
  writer.lit(`<a href="${escapeHtml(node.destination)}"`);
  if (node.title) {
    writer.lit(` title="${escapeHtml(node.title)}"`);
  }
  writer.lit(">");
  renderChildren(node, writer, depth);
  writer.lit("</a>");
}

function renderImage(node: InlineNode, writer: Writer): void {
  const alt = escapeHtml(plainText(node));
  if (!isSafeUrl(node.destination)) {
    writer.lit(alt);
    return;
  }
  writer.lit(`<img src="${escapeHtml(node.destination)}" alt="${alt}"`);
  if (node.title) {
    writer.lit(` title="${escapeHtml(node.title)}"`);
  }
  writer.lit(" />");
}

const WRAPPING_TAGS: Partial<Record<InlineNode["type"], string>> = {
  emph: "em",
  strong: "strong",
  del: "del",
};

function renderInline(node: InlineNode, writer: Writer, depth: number): void {
  if (depth > MAX_INLINE_DEPTH) {
    writer.lit(escapeHtml(plainText(node)));
    return;
  }
  const wrapper = WRAPPING_TAGS[node.type];
  if (wrapper) {
    writer.lit(`<${wrapper}>`);
    renderChildren(node, writer, depth);
    writer.lit(`</${wrapper}>`);
    return;
  }
  switch (node.type) {
    case "text":
      writer.lit(escapeHtml(node.literal));
      break;
    case "softbreak":
      writer.lit("\n");
      break;
    case "hardbreak":
      writer.lit("<br />\n");
      break;
    case "code":
      writer.lit(`<code>${escapeHtml(node.literal)}</code>`);
      break;
    case "html":
      writer.lit(node.literal);
      break;
    case "link":
      renderLink(node, writer, depth);
      break;
    case "image":
      renderImage(node, writer);
      break;
    default:
      renderChildren(node, writer, depth);
  }
}

function renderInlineSource(source: string, context: RenderContext): void {
  renderChildren(
    parseInlines(source, context.refs, context.inline),
    context.writer,
    0
  );
}

function inTightList(block: BlockNode): boolean {
  const list = block.parent?.parent;
  return block.parent?.type === "item" && list?.type === "list" && list.tight;
}

function renderParagraph(block: BlockNode, context: RenderContext): void {
  const { writer } = context;
  if (inTightList(block)) {
    renderInlineSource(block.content.trim(), context);
    return;
  }
  writer.cr();
  writer.lit("<p>");
  renderInlineSource(block.content.trim(), context);
  writer.lit("</p>");
  writer.cr();
}

function renderCode(block: BlockNode, writer: Writer): void {
  const language = block.info.split(RE_WHITESPACE)[0] ?? "";
  let attributes = "";
  if (language) {
    const name = RE_LANGUAGE_PREFIX.test(language)
      ? language
      : `language-${language}`;
    attributes = ` class="${escapeHtml(name)}"`;
  }
  writer.cr();
  writer.lit(
    `<pre><code${attributes}>${escapeHtml(block.content)}</code></pre>`
  );
  writer.cr();
}

function renderList(block: BlockNode, context: RenderContext): void {
  const { writer } = context;
  const data = block.list;
  const tag = data?.ordered ? "ol" : "ul";
  const start =
    data?.ordered && data.start !== 1 ? ` start="${data.start}"` : "";
  writer.cr();
  writer.lit(`<${tag}${start}>`);
  writer.cr();
  for (const item of block.children) {
    writer.lit("<li>");
    renderChildrenBlocks(item, context);
    writer.lit("</li>");
    writer.cr();
  }
  writer.lit(`</${tag}>`);
  writer.cr();
}

function alignAttribute(align: TableAlign | undefined): string {
  return align ? ` align="${align}"` : "";
}

function renderTableRow(
  cells: string[],
  block: BlockNode,
  tag: "th" | "td",
  context: RenderContext
): void {
  const { writer } = context;
  writer.cr();
  writer.lit("<tr>");
  for (const [index, align] of block.align.entries()) {
    writer.cr();
    writer.lit(`<${tag}${alignAttribute(align)}>`);
    renderInlineSource(cells[index] ?? "", context);
    writer.lit(`</${tag}>`);
  }
  writer.cr();
  writer.lit("</tr>");
}

function renderTable(block: BlockNode, context: RenderContext): void {
  const { writer } = context;
  const [header = [], ...body] = block.rows;
  writer.cr();
  writer.lit("<table>");
  writer.cr();
  writer.lit("<thead>");
  renderTableRow(header, block, "th", context);
  writer.cr();
  writer.lit("</thead>");
  if (body.length > 0) {
    writer.cr();
    writer.lit("<tbody>");
    for (const row of body) {
      renderTableRow(row, block, "td", context);
    }
    writer.cr();
    writer.lit("</tbody>");
  }
  writer.cr();
  writer.lit("</table>");
  writer.cr();
}

function renderContainer(
  block: BlockNode,
  tag: string,
  context: RenderContext
): void {
  const { writer } = context;
  writer.cr();
  writer.lit(`<${tag}>`);
  writer.cr();
  renderChildrenBlocks(block, context);
  writer.cr();
  writer.lit(`</${tag}>`);
  writer.cr();
}

function renderBlock(block: BlockNode, context: RenderContext): void {
  const { writer } = context;
  switch (block.type) {
    case "paragraph":
      renderParagraph(block, context);
      break;
    case "heading":
      writer.cr();
      writer.lit(`<h${block.level}>`);
      renderInlineSource(block.content.trim(), context);
      writer.lit(`</h${block.level}>`);
      writer.cr();
      break;
    case "thematic":
      writer.cr();
      writer.lit("<hr />");
      writer.cr();
      break;
    case "blockquote":
      renderContainer(block, "blockquote", context);
      break;
    case "list":
      renderList(block, context);
      break;
    case "code":
      renderCode(block, writer);
      break;
    case "html":
      writer.cr();
      writer.lit(block.content);
      writer.cr();
      break;
    case "table":
      renderTable(block, context);
      break;
    default:
      renderChildrenBlocks(block, context);
  }
}

function renderChildrenBlocks(block: BlockNode, context: RenderContext): void {
  for (const child of block.children) {
    renderBlock(child, context);
  }
}

const NUL = "\u0000";
const REPLACEMENT_CHARACTER = "�";

/**
 * Convert Markdown to HTML. With `allowHtml` off (the default in the tool) every `<`
 * the author typed comes out as `&lt;`; with it on, raw HTML passes through untouched
 * and MUST be run through `sanitizeHtml` before it is shown anywhere.
 */
export function renderMarkdown(
  source: string,
  options: MarkdownOptions
): string {
  const text = source.replaceAll(NUL, REPLACEMENT_CHARACTER);
  const { doc, refs } = parseBlocks(text, {
    gfm: options.gfm,
    html: options.allowHtml,
  });
  const writer = new Writer();
  renderChildrenBlocks(doc, {
    writer,
    refs,
    inline: { gfm: options.gfm, html: options.allowHtml },
  });
  return writer.toString();
}
